/**
 * CoherenceExplainer — an expandable "What's Coherence?" card on the landing
 * page hero, just below the offline credibility badge.
 *
 * Rationale: the Coherence Mode CTA is the highest-intent button on the page,
 * but a cold visitor has no idea what makes it different from a generic
 * "binaural beat" app. This card surfaces the four physiological-realism
 * features behind the heartbeat engine, framed in the same clinical
 * vocabulary that HeartMath / Polar / Whoop use. The goal is two-fold:
 *
 *   1. Educate — give the visitor a concrete reason the heartbeat sounds
 *      "alive" rather than looped, without making them launch the app blind.
 *   2. Signal scientific credibility — these are real cardiology terms.
 *      Wellness customers research before they pay, and matching the
 *      vocabulary they're already using elsewhere lifts trust.
 *
 * UX choices:
 *   - Collapsed by default. The hero must remain scannable; this is a
 *     "tell me more" affordance, not a wall of text.
 *   - Tiny inline SVG sine wave animates regardless of expand state — gives
 *     a non-static visual hook without competing with the AudioReactiveHero
 *     canvas below it.
 *   - Each feature is a single line: the term + a one-sentence plain-English
 *     gloss. Avoids a bulleted-list look.
 *   - Subtle rose/pink accents to visually pair with the "Try Coherence
 *     Mode" hero CTA above (they're conceptually the same offer).
 */
import React, { useState } from 'react';
import { ChevronDown, Heart } from 'lucide-react';

const FEATURES = [
  {
    term: 'Real heart sound',
    gloss: 'Stethoscope-recorded lub-dub, not a synthesised loop.',
  },
  {
    term: 'Heart Rate Variability',
    gloss: 'Beat-to-beat timing & loudness vary like a living heart.',
  },
  {
    term: 'Respiratory Sinus Arrhythmia',
    gloss: 'Heart speeds up on inhale, slows on exhale — the vagal-tone signature.',
  },
  {
    term: '5.5 bpm resonance breath',
    gloss: 'The pacing where HRV peaks. The whole HeartMath-style protocol.',
  },
];

// Tiny inline waveform — pure SVG, CSS animation. No canvas, no JS loop, so
// it's free on perf and pauses respectably with prefers-reduced-motion.
const MiniWave = () => (
  <svg
    width="64"
    height="20"
    viewBox="0 0 64 20"
    fill="none"
    aria-hidden="true"
    className="opacity-70"
  >
    <path
      d="M 0 10 Q 8 2 16 10 T 32 10 T 48 10 T 64 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
      style={{
        strokeDasharray: 200,
        strokeDashoffset: 0,
        animation: 'coherence-wave-flow 3.5s linear infinite',
      }}
    />
    <style>{`
      @keyframes coherence-wave-flow {
        0%   { stroke-dashoffset: 0;   }
        100% { stroke-dashoffset: -32; }
      }
      @media (prefers-reduced-motion: reduce) {
        svg path { animation: none !important; }
      }
    `}</style>
  </svg>
);

export const CoherenceExplainer = () => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="flex justify-center mb-10"
      data-testid="coherence-explainer-root"
    >
      <div className="w-full max-w-2xl">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="coherence-explainer-toggle"
          className="w-full group flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-rose-300/15 bg-rose-500/[0.03] hover:border-rose-300/30 hover:bg-rose-500/[0.06] transition-all text-left"
        >
          <div className="flex items-center gap-3 text-rose-200/80">
            <Heart className="w-4 h-4 fill-rose-400/30 stroke-rose-300" />
            <span className="text-sm font-medium tracking-wide">
              What&apos;s Coherence Mode?
            </span>
            <MiniWave />
          </div>
          <ChevronDown
            className={`w-4 h-4 text-rose-200/60 transition-transform duration-300 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Animated expand. max-height transition is the cheap-but-effective
            pattern for collapsibles when we don't want to install a motion
            library just for one card. The 24rem ceiling comfortably fits all
            four feature rows on every viewport we care about. */}
        <div
          data-testid="coherence-explainer-body"
          className={`overflow-hidden transition-all duration-400 ease-out ${
            open ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0 mt-0'
          }`}
          style={{ transitionProperty: 'max-height, opacity, margin-top' }}
        >
          <div className="px-4 py-4 rounded-xl border border-rose-300/10 bg-gradient-to-b from-rose-500/[0.04] to-transparent">
            <ul className="space-y-2.5">
              {FEATURES.map((f) => (
                <li
                  key={f.term}
                  className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3"
                >
                  <span className="text-rose-200/90 text-sm font-medium whitespace-nowrap">
                    {f.term}
                  </span>
                  <span className="text-white/45 text-xs sm:text-sm leading-relaxed">
                    {f.gloss}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-4 pt-3 border-t border-rose-300/10 text-white/35 text-xs leading-relaxed">
              These are the same physiological markers HeartMath, Polar and
              Whoop track. Coherence Mode brings them into your ears —
              entrain to a real heart, not a metronome.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoherenceExplainer;
